// Target resolution for guide steps: stable attribute hooks, never
// coordinates. Primary hook is data-testid (the codebase-wide convention),
// with data-attr as fallback (the analytics twin stamped on high-value
// actions — see sidebar links, recap actions).

export function queryGuideTarget(testId: string): HTMLElement | null {
  const escaped = CSS.escape(testId);
  return document.querySelector<HTMLElement>(
    `[data-testid="${escaped}"], [data-attr="${escaped}"]`,
  );
}

/**
 * A guide target the overlay can actually anchor to: connected AND not
 * `display:none` / `visibility:hidden`. A responsively-hidden element
 * (`hidden lg:block` → `display:none` below the breakpoint) is present in the
 * DOM but must NOT count as "found" — otherwise the overlay draws a zero-size
 * spotlight and the modal shield swallows every click behind an invisible hole,
 * with no visible target and no failure surfaced. Layout-free (no
 * getBoundingClientRect) so it is meaningful under the real CSS cascade and
 * inert under jsdom, which applies no stylesheet layout.
 */
export function isVisibleTarget(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  const style = getComputedStyle(el);
  return style.display !== "none" && style.visibility !== "hidden";
}

/** `queryGuideTarget` filtered to a currently-visible match (else null). */
function queryVisibleGuideTarget(testId: string): HTMLElement | null {
  const el = queryGuideTarget(testId);
  return el && isVisibleTarget(el) ? el : null;
}

export interface WaitForTargetOptions {
  timeoutMs?: number;
  signal?: AbortSignal;
}

const DEFAULT_TIMEOUT_MS = 8000;

/**
 * Resolve a target element, waiting for it to mount if needed (lists render
 * async, routes lazy-load). Uses a MutationObserver scoped to childList
 * mutations with rAF-batched re-queries — no polling, no per-mutation
 * querySelector storm. Resolves `null` on timeout or abort so the overlay can
 * surface a visible "target not found" state (never a silent skip).
 */
export function waitForGuideTarget(
  testId: string,
  { timeoutMs = DEFAULT_TIMEOUT_MS, signal }: WaitForTargetOptions = {},
): Promise<HTMLElement | null> {
  const immediate = queryVisibleGuideTarget(testId);
  if (immediate) return Promise.resolve(immediate);
  if (signal?.aborted) return Promise.resolve(null);

  return new Promise((resolve) => {
    let rafId: number | null = null;
    let settled = false;

    const observer = new MutationObserver(() => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const el = queryVisibleGuideTarget(testId);
        if (el) settle(el);
      });
    });

    const timeout = setTimeout(() => settle(null), timeoutMs);
    const onAbort = () => settle(null);

    function settle(el: HTMLElement | null): void {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearTimeout(timeout);
      if (rafId !== null) cancelAnimationFrame(rafId);
      signal?.removeEventListener("abort", onAbort);
      resolve(el);
    }

    signal?.addEventListener("abort", onAbort);
    // Watch attribute flips too (class/style/hidden), not just mounts: a target
    // that is present-but-hidden becomes anchorable when its breakpoint/toggle
    // reveals it, and that is an attribute change, not a childList mutation.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden"],
    });
  });
}
